import { apiClient } from '@src/services/http-common'
import {
  AssignDocumentsParams,
  SetDiscardStatusParams,
  DocsAutomation,
  DocsAutomationFiltersType,
  FinalizeDocumentParams,
  LinkDocumentToCreditAppParams,
  UpdateDocumentTagsParams,
  UploadedDocument,
} from '@src/types/automationDocument'
import { DocumentTypesResponse, DocumentWithPages } from '@src/types/documentPage'
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

function updateDocsAutomationInCache(queryClient: QueryClient, updatedDocument: DocsAutomation) {
  queryClient.setQueriesData(
    {
      predicate: (query) => {
        const [key] = query.queryKey as [string, DocsAutomationFiltersType]
        return key === 'docsAutomation'
      },
    },
    (oldData: DocsAutomation[] | undefined) => {
      if (!oldData) return oldData
      return oldData.map((doc) => (doc.id === updatedDocument.id ? updatedDocument : doc))
    },
  )
}

export function invalidateDocsAutomationQueries(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const [key] = query.queryKey as [string, DocsAutomationFiltersType]
      return key === 'docsAutomation'
    },
  })
}

export function invalidateFullDocumentCached(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const [key] = query.queryKey as [string, DocsAutomationFiltersType]
      return key === 'fullDocument'
    },
  })
}

async function getDocument(documentId: string) {
  const response = await apiClient.get<DocumentWithPages>(`/documents/${documentId}`)
  return response.data
}

const PROCESSING_STATUSES = new Set(['pending', 'pendingbatch', 'stored', 'processing', 'classified', 'extracted'])

export function useDocument(documentId?: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fullDocument', documentId],
    queryFn: () => getDocument(documentId!),
    enabled: !!documentId,
    refetchInterval: (query) => {
      const status = query.state.data?.document?.processingStatus
      if (!status || !PROCESSING_STATUSES.has(status)) return false
      return 10_000
    },
  })

  return [data, isLoading, error] as const
}

export function useDocumentsFromCache() {
  const queryClient = useQueryClient()

  const cachedData = queryClient.getQueriesData<DocsAutomation[]>({
    predicate: (query) => {
      const [key] = query.queryKey as [string]
      return key === 'docsAutomation'
    },
  })

  const documents: DocsAutomation[] = []
  cachedData.forEach(([, data]) => {
    if (data) {
      documents.push(...data)
    }
  })

  const uniqueDocs = Array.from(new Map(documents.map((doc) => [doc.id, doc])).values())
  return uniqueDocs
}

async function getDocumentTypes() {
  const response = await apiClient.get<DocumentTypesResponse>('/documents/types')
  return response.data
}

export function useDocumentTypes() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['documentTypes'],
    queryFn: getDocumentTypes,
    staleTime: Infinity,
  })

  return [data, isLoading, error] as const
}

async function uploadDocuments(files: File | File[]) {
  const formData = new FormData()
  const fileArray = Array.isArray(files) ? files : [files]

  fileArray.forEach((file) => {
    formData.append('files', file)
  })

  const response = await apiClient.post<DocumentWithPages>('/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

export function useUploadDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (files: File | File[]) => uploadDocuments(files),
    onSuccess: (data) => {
      invalidateDocsAutomationQueries(queryClient)
      // Seed the detail page cache so it renders immediately after navigation
      if (data?.document?.id) {
        queryClient.setQueryData(['fullDocument', data.document.id], data)
      }
    },
  })
}

async function getDocsAutomationByFilters(filters: DocsAutomationFiltersType) {
  const response = await apiClient.post<DocsAutomation[]>('/documents/filters', filters)
  return response.data ?? []
}

export function useDocsAutomationByFilters(
  filters: DocsAutomationFiltersType,
): [DocsAutomation[], boolean, Error | null, () => void] {
  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ['docsAutomation', filters],
    queryFn: () => getDocsAutomationByFilters(filters),
    enabled: !!filters,
    refetchInterval: 30_000,
  })

  return [data ?? [], isFetching, error, () => void refetch()]
}

function useDocumentMutation<TParams>(
  mutationFn: (params: TParams) => Promise<DocsAutomation>,
  { invalidateFullDocument = false }: { invalidateFullDocument?: boolean } = {},
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: (updatedDocument) => {
      updateDocsAutomationInCache(queryClient, updatedDocument)
      invalidateDocsAutomationQueries(queryClient)
      if (invalidateFullDocument) invalidateFullDocumentCached(queryClient)
    },
  })
}

async function setDiscardStatus(params: SetDiscardStatusParams) {
  const { documentId, ...body } = params
  const { data } = await apiClient.put<DocsAutomation>(`/documents/${documentId}/isDiscarded`, body)
  return data
}

export function useSetDiscardStatus() {
  return useDocumentMutation(setDiscardStatus, { invalidateFullDocument: true })
}

export interface DocsAutomationSearchPossibleMatchFilters {
  referenceNumber: string | null
  email: string | null
}

async function unlinkDocsAutomation(id: string) {
  const { data } = await apiClient.delete<DocsAutomation>(`/documents/${id}/unlinkCreditApplication`)
  return data
}

async function linkDocumentToCreditApp(params: LinkDocumentToCreditAppParams) {
  const { documentId, ...body } = params
  const { data } = await apiClient.put<DocsAutomation>(`/documents/${documentId}/CreditApplicationLink`, body)
  return data
}

async function replaceDocumentLink(params: LinkDocumentToCreditAppParams) {
  const { documentId, ...body } = params
  const { data } = await apiClient.post<DocsAutomation>(`/documents/${documentId}/replaceCreditApplicationLink`, body)
  return data
}

async function finalizeDocument(params: FinalizeDocumentParams) {
  const { documentId, ...body } = params
  const { data } = await apiClient.put<DocsAutomation>(`/documents/${documentId}/finalize`, body)
  return data
}

async function updateDocumentTags(params: UpdateDocumentTagsParams) {
  const { documentId, ...body } = params
  const { data } = await apiClient.put<DocsAutomation>(`/documents/${documentId}/tags`, body)
  return data
}

async function assignDocuments(params: AssignDocumentsParams) {
  const { data } = await apiClient.post<DocsAutomation[]>('/documents/assign', params)
  return data
}

export function useFinalizeDocument() {
  return useDocumentMutation(finalizeDocument, { invalidateFullDocument: true })
}

export function useUnlinkDocsAutomation() {
  return useDocumentMutation(unlinkDocsAutomation, { invalidateFullDocument: true })
}

export function useLinkDocumentToCreditApp() {
  return useDocumentMutation(linkDocumentToCreditApp, { invalidateFullDocument: true })
}

export function useReplaceDocumentLink() {
  return useDocumentMutation(replaceDocumentLink, { invalidateFullDocument: true })
}

export function useUpdateDocumentTags() {
  return useDocumentMutation(updateDocumentTags)
}

async function getUploadedDocuments(documentId: string) {
  const response = await apiClient.get<UploadedDocument[]>(`/documents/${documentId}/uploaded`)
  return response.data
}

export function useUploadedDocuments(documentId?: string, enabled = true) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['uploadedDocuments', documentId],
    queryFn: () => getUploadedDocuments(documentId!),
    enabled: !!documentId && enabled,
  })

  return [data ?? [], isLoading, error] as const
}

export function useAssignDocuments() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: assignDocuments,
    onSuccess: () => {
      invalidateDocsAutomationQueries(queryClient)
    },
  })
}

// Retry document processing
export type RetryMode = 'Full' | 'ClassificationOnly' | 'MatchingOnly'

export interface RetryDocumentParams {
  documentId: string
  versionTag: string
  mode: RetryMode
}

async function retryDocument(params: RetryDocumentParams) {
  const { documentId, ...body } = params
  await apiClient.post(`/documents/${documentId}/retry`, body)
}

export function useRetryDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: retryDocument,
    onSuccess: () => {
      invalidateDocsAutomationQueries(queryClient)
      invalidateFullDocumentCached(queryClient)
    },
  })
}
